// Jun-2026 — WP-B: central Labour Library. Aadhaar is masked by default
// (XXXXXXXX1234) — the backend never sends the full value without the
// audited view_full_aadhaar permission + a mandatory purpose. One active
// project assignment per labourer, enforced server-side.
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Pencil, Eye, History, FileSpreadsheet, UserCheck, UserMinus, Ban, CheckCircle2, Settings2, Camera } from "lucide-react";
import { compressImage } from "@/lib/imageCompress";
import { SearchableSelect } from "@/components/SearchableSelect";

const inputCls = "border border-slate-300 rounded px-2 py-1.5 text-xs w-full";
const today = () => new Date().toLocaleDateString("en-CA");

const photoCache = new Map();
function LabourAvatar({ row, size = 28 }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!row.has_photo) { setUrl(null); return; }
    const key = `${row.id}:${row.photo_updated_at}`;
    if (!photoCache.has(key)) {
      photoCache.set(key, api.get(`/labour-library/${row.id}/photo`, { responseType: "blob" })
        .then((r) => URL.createObjectURL(r.data)).catch(() => null));
    }
    let alive = true;
    photoCache.get(key).then((u) => { if (alive) setUrl(u); });
    return () => { alive = false; };
  }, [row.id, row.has_photo, row.photo_updated_at]);
  if (url) return <img src={url} alt={row.name} style={{ width: size, height: size }}
    className="rounded-full object-cover border border-slate-200 shrink-0" data-testid={`labour-avatar-${row.labour_code}`} />;
  return <div style={{ width: size, height: size }} data-testid={`labour-avatar-${row.labour_code}`}
    className="rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-[10px] font-bold shrink-0">
    {(row.name || "?").charAt(0).toUpperCase()}</div>;
}

function LabourFormModal({ masters, editRow, onClose, onSaved, onMastersChanged }) {
  const { hasPerm } = useAuth();
  const canQuickAddTag = hasPerm("labour_library", "edit");
  const [f, setF] = useState({
    name: editRow?.name || "", mobile: editRow?.mobile || "",
    aadhaar: "", address: editRow?.address || "", city: editRow?.city || "",
    skill_category_id: editRow?.skill_category_id || "",
    labour_type_id: editRow?.labour_type_id || "",
    tag: editRow?.tag || "",
    per_day_amount: editRow?.per_day_amount ?? "" });
  const [allowDupMobile, setAllowDupMobile] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  // Jun-2026 — Tag master: searchable pick + permission-gated quick-add.
  const [addingTag, setAddingTag] = useState(false);
  const [newTag, setNewTag] = useState("");
  const tagItems = (() => {
    const list = (masters.tags || []).filter((m) => m.active !== false);
    if (f.tag && !list.some((m) => m.name === f.tag))
      return [{ id: `legacy-${f.tag}`, name: f.tag }, ...list];
    return list;
  })();
  const createTag = async () => {
    const name = newTag.trim();
    if (!name) return toast.error("Enter the tag name");
    try {
      const r = await api.post("/labour-masters/tags", { name });
      toast.success(`Tag "${r.data.name}" added`);
      setF((x) => ({ ...x, tag: r.data.name }));
      setAddingTag(false); setNewTag("");
      onMastersChanged && onMastersChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Tag add failed"); }
  };

  useEffect(() => {
    if (editRow?.has_photo) {
      api.get(`/labour-library/${editRow.id}/photo`, { responseType: "blob" })
        .then((r) => setPreview(URL.createObjectURL(r.data))).catch(() => {});
    }
  }, [editRow]);

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPhotoFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const submit = async () => {
    if (!f.tag.trim()) return toast.error("Tag is mandatory — please fill the Tag field");
    setBusy(true);
    try {
      const payload = { ...f, allow_duplicate_mobile: allowDupMobile };
      if (editRow && !f.aadhaar) delete payload.aadhaar;
      let id = editRow?.id;
      if (editRow) await api.put(`/labour-library/${editRow.id}`, payload);
      else { const r = await api.post("/labour-library", payload); id = r.data.id; }
      if (photoFile) {
        const small = await compressImage(photoFile);
        const fd = new FormData();
        fd.append("file", small);
        await api.post(`/labour-library/${id}/photo`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(editRow ? "Labour updated" : "Labour created");
      onSaved(); onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed";
      toast.error(msg);
      if (e?.response?.status === 409 && msg.includes("Mobile")) setAllowDupMobile(false);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10" data-testid="labour-form-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="text-sm font-bold text-slate-900">{editRow ? `Edit ${editRow.labour_code}` : "New Labour"}</div>
          <button data-testid="labour-form-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 text-xs">
          <div className="flex items-center gap-3">
            {preview
              ? <img src={preview} alt="labour" className="w-16 h-16 rounded-full object-cover border border-slate-200" data-testid="labour-photo-preview" />
              : <div className="w-16 h-16 rounded-full bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center text-slate-400"><Camera size={18} /></div>}
            <label className="text-[11px] font-bold text-emerald-800 cursor-pointer hover:underline" data-testid="labour-photo-label">
              {preview ? "Replace photo" : "Upload photo (optional)"}
              <input data-testid="labour-photo-input" type="file" accept="image/*" className="hidden" onChange={pickPhoto} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1"><span className="font-bold text-slate-600">Labour Name *</span>
              <input data-testid="labour-name-input" className={inputCls} value={f.name} onChange={set("name")} /></label>
            <label className="space-y-1"><span className="font-bold text-slate-600">Mobile Number *</span>
              <input data-testid="labour-mobile-input" className={inputCls} value={f.mobile} onChange={set("mobile")} /></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1"><span className="font-bold text-slate-600">Aadhaar Number <span className="font-normal text-slate-400">(optional{editRow ? " — blank keeps unchanged" : ""})</span></span>
              <input data-testid="labour-aadhaar-input" className={inputCls} placeholder={editRow?.aadhaar_masked && editRow.aadhaar_masked !== "—" ? editRow.aadhaar_masked : "12 digits"} value={f.aadhaar} onChange={set("aadhaar")} />
              <span className="text-[10px] text-slate-400">Encrypted at rest · always displayed masked</span></label>
            <label className="space-y-1"><span className="font-bold text-slate-600">Labour City <span className="font-normal text-slate-400">(optional)</span></span>
              <input data-testid="labour-city-input" className={inputCls} value={f.city} onChange={set("city")} /></label>
          </div>
          <label className="block space-y-1"><span className="font-bold text-slate-600">Address <span className="font-normal text-slate-400">(optional)</span></span>
            <textarea data-testid="labour-address-input" className={`${inputCls} h-14`} value={f.address} onChange={set("address")} /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1"><span className="font-bold text-slate-600">Skill Category <span className="font-normal text-slate-400">(optional)</span></span>
              <select data-testid="labour-skill-select" className={inputCls} value={f.skill_category_id} onChange={set("skill_category_id")}>
                <option value="">— select —</option>
                {(masters.skill_categories || []).filter((m) => m.active !== false).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select></label>
            <label className="space-y-1"><span className="font-bold text-slate-600">Labour Type <span className="font-normal text-slate-400">(optional)</span></span>
              <select data-testid="labour-type-select" className={inputCls} value={f.labour_type_id} onChange={set("labour_type_id")}>
                <option value="">— select —</option>
                {(masters.labour_types || []).filter((m) => m.active !== false).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1"><span className="font-bold text-slate-600">Per Day Amount (₹) <span className="font-normal text-slate-400">(optional — wage payments; day-wise override possible later)</span></span>
              <input data-testid="labour-perday-input" type="number" min="0" step="any" className={inputCls} value={f.per_day_amount} onChange={set("per_day_amount")} /></label>
            <label className="space-y-1"><span className="font-bold text-slate-600">Tag * <span className="font-normal text-slate-400">(from the Labour Tags master)</span></span>
              <SearchableSelect testid="labour-tag-select" className="w-full"
                items={tagItems} value={f.tag}
                getKey={(m) => m.name}
                onChange={(name) => setF((x) => ({ ...x, tag: name || "" }))}
                placeholder="— select tag —" searchPlaceholder="Search tags…" />
              {canQuickAddTag && (addingTag ? (
                <div className="flex gap-1">
                  <input data-testid="labour-tag-new-input" className={inputCls} placeholder="New tag name…" autoFocus
                    value={newTag} onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && createTag()} />
                  <button type="button" data-testid="labour-tag-new-save" onClick={createTag}
                    className="px-2.5 py-1 bg-emerald-700 text-white font-bold rounded text-xs">Add</button>
                  <button type="button" onClick={() => { setAddingTag(false); setNewTag(""); }}
                    className="text-slate-400 hover:text-slate-700"><X size={12} /></button>
                </div>
              ) : (
                <button type="button" data-testid="labour-tag-quickadd-btn" onClick={() => setAddingTag(true)}
                  className="text-[10px] font-bold text-emerald-700 hover:underline">+ New tag</button>
              ))}
            </label>
          </div>
          <label className="flex items-center gap-2 text-[11px] text-slate-600">
            <input type="checkbox" data-testid="labour-dup-mobile-chk" checked={allowDupMobile} onChange={(e) => setAllowDupMobile(e.target.checked)} />
            Allow duplicate mobile (shared phone — deliberate)
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-3 py-1.5 border rounded">Cancel</button>
            <button data-testid="labour-form-submit" disabled={busy} onClick={submit}
              className="px-4 py-1.5 bg-emerald-700 text-white font-bold rounded disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MasterSection({ kind, title, rows, onChanged }) {
  const [name, setName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const add = async () => {
    if (!name.trim()) return toast.error("Enter a name");
    try { await api.post(`/labour-masters/${kind}`, { name: name.trim() }); toast.success("Added"); setName(""); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };
  const rename = async (mid) => {
    if (!editName.trim()) return toast.error("Name cannot be empty");
    try { await api.put(`/labour-masters/${kind}/${mid}`, { name: editName.trim() }); toast.success("Renamed"); setEditId(null); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
  };
  const toggle = async (m) => {
    try { await api.put(`/labour-masters/${kind}/${m.id}`, { active: m.active === false }); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  return (
    <div className="flex-1 min-w-[220px]" data-testid={`master-section-${kind}`}>
      <div className="text-[10px] uppercase font-bold text-slate-400 mb-1">{title}</div>
      <div className="flex gap-1 mb-2">
        <input data-testid={`master-add-input-${kind}`} className={inputCls}
          placeholder={kind === "skill-categories" ? "New skill category…" : kind === "tags" ? "New tag…" : "New labour type…"}
          value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
        <button data-testid={`master-add-btn-${kind}`} onClick={add}
          className="px-2.5 py-1 bg-emerald-700 text-white font-bold rounded text-xs">Add</button>
      </div>
      <div className="border border-slate-200 rounded divide-y divide-slate-100 max-h-64 overflow-y-auto">
        {(rows || []).map((m) => (
          <div key={m.id} className="flex items-center gap-2 px-2 py-1.5" data-testid={`master-row-${m.id}`}>
            {editId === m.id ? (
              <>
                <input className={inputCls} value={editName} onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && rename(m.id)} autoFocus />
                <button data-testid={`master-rename-save-${m.id}`} onClick={() => rename(m.id)} className="text-emerald-700 font-bold">Save</button>
                <button onClick={() => setEditId(null)} className="text-slate-400 hover:text-slate-700"><X size={12} /></button>
              </>
            ) : (
              <>
                <span className={`flex-1 ${m.active === false ? "text-slate-400 line-through" : ""}`}>{m.name}</span>
                <button title="Rename" onClick={() => { setEditId(m.id); setEditName(m.name); }}
                  className="text-slate-500 hover:text-slate-800"><Pencil size={11} /></button>
                <button data-testid={`master-toggle-${m.id}`} onClick={() => toggle(m)}
                  className={`text-[10px] font-bold ${m.active === false ? "text-emerald-700" : "text-slate-400 hover:text-red-600"}`}>
                  {m.active === false ? "Activate" : "Deactivate"}</button>
              </>
            )}
          </div>
        ))}
        {(rows || []).length === 0 && <div className="px-2 py-4 text-center text-slate-400">No entries yet.</div>}
      </div>
    </div>
  );
}

function MastersModal({ masters, onClose, onChanged }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10" data-testid="labour-masters-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="text-sm font-bold text-slate-900">Labour Masters — Skill Categories, Labour Types & Tags</div>
          <button data-testid="labour-masters-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        <div className="p-5 flex gap-6 flex-wrap text-xs">
          <MasterSection kind="skill-categories" title="Skill Categories" rows={masters.skill_categories} onChanged={onChanged} />
          <MasterSection kind="labour-types" title="Labour Types" rows={masters.labour_types} onChanged={onChanged} />
          <MasterSection kind="tags" title="Labour Tags" rows={masters.tags} onChanged={onChanged} />
        </div>
        <div className="px-5 pb-4 text-[10px] text-slate-400">Deactivated values stay on existing labour records but cannot be selected for new entries.</div>
      </div>
    </div>
  );
}

function AssignModal({ row, onClose, onDone }) {
  const [projects, setProjects] = useState([]);
  const [f, setF] = useState({ project_id: "", assigned_from: today(), remark: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/projects-picker").then((r) => {
      const list = r.data?.items || r.data?.rows || (Array.isArray(r.data) ? r.data : []);
      setProjects(list.filter((p) => p.status === "Active" && p.project_ops_enabled));
    }).catch(() => toast.error("Could not load projects"));
  }, []);
  const submit = async () => {
    if (!f.project_id) return toast.error("Select a project");
    if (!f.remark.trim()) return toast.error("Assignment remark is mandatory");
    setBusy(true);
    try {
      await api.post(`/labour-library/${row.id}/assign`, f);
      toast.success(`${row.labour_code} assigned`);
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Assignment failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center py-16" data-testid="labour-assign-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-5 space-y-3 text-xs">
        <div className="text-sm font-bold text-slate-900">Assign {row.labour_code} — {row.name}</div>
        <div className="text-[10px] text-slate-500">One active project at a time. Active + Project-Ops-enabled projects only.</div>
        <SearchableSelect
          value={f.project_id}
          onChange={(id) => setF({ ...f, project_id: id || "" })}
          items={projects}
          getLabel={(p) => `${p.project_code} — ${p.project_name}`}
          getSearchText={(p) => `${p.project_code} ${p.project_name}`}
          placeholder="— select project —"
          searchPlaceholder="Search project code / name…"
          testid="assign-project-select"
        />
        <label className="block space-y-1"><span className="font-bold text-slate-600">Assigned From *</span>
          <input data-testid="assign-from-input" type="date" className={inputCls} value={f.assigned_from} onChange={(e) => setF({ ...f, assigned_from: e.target.value })} /></label>
        <textarea data-testid="assign-remark-input" className={`${inputCls} h-14`} placeholder="Assignment remark (mandatory)" value={f.remark} onChange={(e) => setF({ ...f, remark: e.target.value })} />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 border rounded">Cancel</button>
          <button data-testid="assign-confirm-btn" disabled={busy} onClick={submit}
            className="px-4 py-1.5 bg-emerald-700 text-white font-bold rounded disabled:opacity-50">{busy ? "Assigning…" : "Assign"}</button>
        </div>
      </div>
    </div>
  );
}

function ReleaseModal({ row, onClose, onDone }) {
  const [f, setF] = useState({ released_on: today(), remark: "" });
  const [busy, setBusy] = useState(false);
  const a = row.current_assignment;
  const submit = async () => {
    if (!f.remark.trim()) return toast.error("Release remark is mandatory");
    setBusy(true);
    try {
      await api.post(`/labour-assignments/${a.assignment_id}/release`, f);
      toast.success(`${row.labour_code} released from ${a.project_code}`);
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Release failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center py-16" data-testid="labour-release-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-5 space-y-3 text-xs">
        <div className="text-sm font-bold text-slate-900">Release {row.labour_code} from {a?.project_code}?</div>
        <label className="block space-y-1"><span className="font-bold text-slate-600">Released On *</span>
          <input data-testid="release-date-input" type="date" className={inputCls} value={f.released_on} onChange={(e) => setF({ ...f, released_on: e.target.value })} /></label>
        <textarea data-testid="release-remark-input" className={`${inputCls} h-14`} placeholder="Release remark (mandatory)" value={f.remark} onChange={(e) => setF({ ...f, remark: e.target.value })} />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 border rounded">Cancel</button>
          <button data-testid="release-confirm-btn" disabled={busy} onClick={submit}
            className="px-4 py-1.5 bg-orange-600 text-white font-bold rounded disabled:opacity-50">{busy ? "Releasing…" : "Release"}</button>
        </div>
      </div>
    </div>
  );
}

function RevealModal({ row, onClose }) {
  const [purpose, setPurpose] = useState("");
  const [value, setValue] = useState("");
  const reveal = async () => {
    if (!purpose.trim()) return toast.error("Purpose is mandatory (audited)");
    try {
      const r = await api.get(`/labour-library/${row.id}/aadhaar`, { params: { purpose: purpose.trim() } });
      setValue(r.data.aadhaar);
    } catch (e) { toast.error(e?.response?.data?.detail || "Access denied"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center py-16" data-testid="aadhaar-reveal-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-5 space-y-3 text-xs">
        <div className="text-sm font-bold text-slate-900">Full Aadhaar — {row.labour_code}</div>
        <div className="text-[10px] text-amber-700">Every reveal is audited with your identity, purpose and time.</div>
        {value ? (
          <div className="text-lg font-mono font-bold text-center py-2 bg-slate-50 rounded" data-testid="aadhaar-full-value">{value}</div>
        ) : (
          <>
            <input data-testid="aadhaar-purpose-input" className={inputCls} placeholder="Purpose (mandatory)" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
            <button data-testid="aadhaar-reveal-btn" onClick={reveal} className="w-full px-3 py-1.5 bg-slate-800 text-white font-bold rounded">Reveal</button>
          </>
        )}
        <button onClick={onClose} className="w-full px-3 py-1.5 border rounded">Close</button>
      </div>
    </div>
  );
}

function HistoryModal({ row, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/labour-library/${row.id}/assignments`).then((r) => setRows(r.data.rows || []))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Access denied"); setRows([]); });
  }, [row.id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10" data-testid="labour-history-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="text-sm font-bold text-slate-900">{row.labour_code} — assignment history</div>
          <button data-testid="labour-history-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        <div className="p-5 text-xs">
          <table className="w-full" data-testid="labour-history-table">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
              <tr><th className="px-2 py-1.5 text-left">Project</th><th className="px-2 py-1.5 text-left">From</th>
                <th className="px-2 py-1.5 text-left">Released</th><th className="px-2 py-1.5 text-left">Status</th>
                <th className="px-2 py-1.5 text-left">Remarks</th><th className="px-2 py-1.5 text-left">By</th></tr>
            </thead>
            <tbody>
              {(rows || []).map((a) => (
                <tr key={a.id} className="border-t border-slate-100">
                  <td className="px-2 py-1.5">{a.project_code} — {a.project_name}</td>
                  <td className="px-2 py-1.5">{a.assigned_from}</td>
                  <td className="px-2 py-1.5">{a.released_on || "—"}</td>
                  <td className="px-2 py-1.5">{a.status === "Active" ? <span className="text-emerald-700 font-bold">Active</span> : "Released"}</td>
                  <td className="px-2 py-1.5 max-w-[200px] truncate" title={`${a.assignment_remark} ${a.release_remark}`}>{a.assignment_remark}{a.release_remark ? ` · ${a.release_remark}` : ""}</td>
                  <td className="px-2 py-1.5">{a.assigned_by}</td>
                </tr>
              ))}
              {rows && rows.length === 0 && <tr><td colSpan={6} className="px-2 py-6 text-center text-slate-400">No assignments yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatusDialog({ row, onClose, onDone }) {
  const [remark, setRemark] = useState("");
  const target = row.status === "Active" ? "Inactive" : "Active";
  const submit = async () => {
    if (!remark.trim()) return toast.error("Remark is mandatory");
    try {
      await api.post(`/labour-library/${row.id}/status`, { status: target, remark: remark.trim() });
      toast.success(`${row.labour_code} → ${target}`);
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Status change failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center py-16" data-testid="labour-status-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-5 space-y-3 text-xs">
        <div className="text-sm font-bold text-slate-900">Mark {row.labour_code} as {target}?</div>
        <textarea data-testid="labour-status-remark" className={`${inputCls} h-14`} placeholder="Remark (mandatory)" value={remark} onChange={(e) => setRemark(e.target.value)} />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 border rounded">Cancel</button>
          <button data-testid="labour-status-confirm" onClick={submit} className="px-4 py-1.5 bg-slate-800 text-white font-bold rounded">Confirm</button>
        </div>
      </div>
    </div>
  );
}

export default function LabourLibraryPage() {
  const { hasPerm } = useAuth();
  const canCreate = hasPerm("labour_library", "create");
  const canEdit = hasPerm("labour_library", "edit");
  const canStatus = hasPerm("labour_library", "inactivate");
  // "Create = assign · Edit = release" (permission guide); legacy custom
  // grants assign/release stay honoured.
  const canAssign = hasPerm("labour_assignment", "create") || hasPerm("labour_assignment", "assign");
  const canRelease = hasPerm("labour_assignment", "edit") || hasPerm("labour_assignment", "release");
  const canHistory = hasPerm("labour_assignment", "view");
  const canReveal = hasPerm("view_full_aadhaar", "view");

  const [masters, setMasters] = useState({});
  const [data, setData] = useState({ rows: [], total: 0 });
  const [page, setPage] = useState(1);
  const [flt, setFlt] = useState({ q: "", status: "", skill_category_id: "", labour_type_id: "" });
  const [modal, setModal] = useState(null); // {type, row}
  const limit = 25;

  const loadMasters = useCallback(() => {
    api.get("/labour-masters").then((r) => setMasters(r.data || {})).catch(() => {});
  }, []);
  useEffect(() => { loadMasters(); }, [loadMasters]);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/labour-library", { params: {
        page, limit, q: flt.q || undefined, status: flt.status || undefined,
        skill_category_id: flt.skill_category_id || undefined,
        labour_type_id: flt.labour_type_id || undefined } });
      setData(r.data || {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load Labour Library"); }
  }, [page, flt]);
  useEffect(() => { load(); }, [load]);

  const doExport = async () => {
    try {
      const r = await api.get("/labour-library-export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = "labour_library.xlsx"; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
  };

  return (
    <div className="space-y-3" data-testid="labour-library-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-lg font-bold text-slate-900">Labour Library</h1>
          <div className="text-[11px] text-slate-500">Central labour master · Aadhaar always masked by default · one active project per labourer</div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button data-testid="labour-masters-btn" onClick={() => setModal({ type: "masters" })}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-slate-700 border border-slate-300 rounded hover:bg-slate-50">
              <Settings2 size={13} /> Masters</button>
          )}
          <button data-testid="labour-export-btn" onClick={doExport}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-emerald-800 border border-emerald-300 rounded hover:bg-emerald-50">
            <FileSpreadsheet size={13} /> Export</button>
          {canCreate && (
            <button data-testid="labour-create-btn" onClick={() => setModal({ type: "form" })}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold bg-emerald-700 text-white rounded hover:bg-emerald-800">
              <Plus size={13} /> New Labour</button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap text-xs">
        <input data-testid="labour-search-input" className="border border-slate-300 rounded px-2 py-1.5 w-64"
          placeholder="Search ID / name / mobile / Aadhaar last-4…" value={flt.q}
          onChange={(e) => { setFlt({ ...flt, q: e.target.value }); setPage(1); }} />
        <select data-testid="labour-filter-status" className="border border-slate-300 rounded px-2 py-1.5" value={flt.status}
          onChange={(e) => { setFlt({ ...flt, status: e.target.value }); setPage(1); }}>
          <option value="">All statuses</option><option>Active</option><option>Inactive</option>
        </select>
        <select data-testid="labour-filter-skill" className="border border-slate-300 rounded px-2 py-1.5" value={flt.skill_category_id}
          onChange={(e) => { setFlt({ ...flt, skill_category_id: e.target.value }); setPage(1); }}>
          <option value="">All skills</option>
          {(masters.skill_categories || []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <select data-testid="labour-filter-type" className="border border-slate-300 rounded px-2 py-1.5" value={flt.labour_type_id}
          onChange={(e) => { setFlt({ ...flt, labour_type_id: e.target.value }); setPage(1); }}>
          <option value="">All types</option>
          {(masters.labour_types || []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>

      <div className="border border-slate-200 rounded overflow-x-auto">
        <table className="w-full text-xs" data-testid="labour-table">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Labour ID</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Mobile</th>
              <th className="px-3 py-2 text-left">City</th>
              <th className="px-3 py-2 text-left">Aadhaar</th>
              <th className="px-3 py-2 text-left">Skill</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Tag</th>
              <th className="px-3 py-2 text-right">Per Day ₹</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Current Project</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(data.rows || []).length === 0 && (
              <tr><td colSpan={12} className="px-3 py-8 text-center text-slate-400" data-testid="labour-empty">No labour records found.</td></tr>
            )}
            {(data.rows || []).map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`labour-row-${r.labour_code}`}>
                <td className="px-3 py-2 font-mono font-bold">{r.labour_code}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <LabourAvatar row={r} />
                    <span>{r.name}</span>
                  </div>
                </td>
                <td className="px-3 py-2">{r.mobile}</td>
                <td className="px-3 py-2" data-testid={`labour-city-${r.labour_code}`}>{r.city || "—"}</td>
                <td className="px-3 py-2 font-mono">
                  <span data-testid={`labour-aadhaar-${r.labour_code}`}>{r.aadhaar_masked}</span>
                  {canReveal && r.aadhaar_masked !== "—" && (
                    <button data-testid={`labour-reveal-${r.labour_code}`} title="Reveal (audited)" onClick={() => setModal({ type: "reveal", row: r })}
                      className="ml-1 text-slate-400 hover:text-slate-800"><Eye size={12} /></button>
                  )}
                </td>
                <td className="px-3 py-2">{r.skill_category}</td>
                <td className="px-3 py-2">{r.labour_type}</td>
                <td className="px-3 py-2" data-testid={`labour-tag-${r.labour_code}`}>
                  {r.tag ? <span className="inline-block bg-indigo-50 border border-indigo-200 text-indigo-800 rounded px-1.5 py-0.5 text-[10px] font-bold">{r.tag}</span> : "—"}
                </td>
                <td className="px-3 py-2 text-right tabular-nums" data-testid={`labour-perday-${r.labour_code}`}>{r.per_day_amount != null ? `₹ ${r.per_day_amount}` : "—"}</td>
                <td className="px-3 py-2">{r.status === "Active"
                  ? <span className="text-emerald-700 font-bold">Active</span>
                  : <span className="text-slate-400 font-bold">Inactive</span>}</td>
                <td className="px-3 py-2" data-testid={`labour-project-${r.labour_code}`}>
                  {r.current_assignment ? `${r.current_assignment.project_code}` : "—"}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap space-x-2">
                  {canHistory && <button data-testid={`labour-history-${r.labour_code}`} title="Assignment history" onClick={() => setModal({ type: "history", row: r })}
                    className="text-sky-700 hover:underline inline-flex items-center gap-0.5"><History size={12} /></button>}
                  {canEdit && <button data-testid={`labour-edit-${r.labour_code}`} title="Edit" onClick={() => setModal({ type: "form", row: r })}
                    className="text-slate-600 hover:text-slate-900 inline-flex items-center gap-0.5"><Pencil size={12} /></button>}
                  {canAssign && r.status === "Active" && !r.current_assignment && (
                    <button data-testid={`labour-assign-${r.labour_code}`} onClick={() => setModal({ type: "assign", row: r })}
                      className="text-emerald-800 font-bold hover:underline inline-flex items-center gap-0.5"><UserCheck size={12} /> Assign</button>)}
                  {canRelease && r.current_assignment && (
                    <button data-testid={`labour-release-${r.labour_code}`} onClick={() => setModal({ type: "release", row: r })}
                      className="text-orange-700 font-bold hover:underline inline-flex items-center gap-0.5"><UserMinus size={12} /> Release</button>)}
                  {canStatus && (
                    <button data-testid={`labour-status-${r.labour_code}`} title={r.status === "Active" ? "Inactivate" : "Activate"}
                      onClick={() => setModal({ type: "status", row: r })}
                      className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-0.5">
                      {r.status === "Active" ? <Ban size={12} /> : <CheckCircle2 size={12} />}</button>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.total > limit && (
        <div className="flex items-center justify-end gap-2 text-xs">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-2 py-1 border rounded disabled:opacity-40">Prev</button>
          <span>Page {page} / {Math.ceil(data.total / limit)}</span>
          <button disabled={page >= Math.ceil(data.total / limit)} onClick={() => setPage(page + 1)} className="px-2 py-1 border rounded disabled:opacity-40">Next</button>
        </div>
      )}

      {modal?.type === "form" && <LabourFormModal masters={masters} editRow={modal.row} onClose={() => setModal(null)} onSaved={load} onMastersChanged={loadMasters} />}
      {modal?.type === "assign" && <AssignModal row={modal.row} onClose={() => setModal(null)} onDone={load} />}
      {modal?.type === "release" && <ReleaseModal row={modal.row} onClose={() => setModal(null)} onDone={load} />}
      {modal?.type === "reveal" && <RevealModal row={modal.row} onClose={() => setModal(null)} />}
      {modal?.type === "history" && <HistoryModal row={modal.row} onClose={() => setModal(null)} />}
      {modal?.type === "status" && <StatusDialog row={modal.row} onClose={() => setModal(null)} onDone={load} />}
      {modal?.type === "masters" && <MastersModal masters={masters} onClose={() => setModal(null)} onChanged={loadMasters} />}
    </div>
  );
}
